import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchConfig, updateConfig } from "../../../../../../store/actions/configActions";
import { getConfig, saveConfig } from '../../../../../../utils/configStorage';
import ButtonComponent from '../../../../../../components/general/Button/Button';
import TitlePageComponent from '../../../../../../components/general/TitlePage/TitlePage';
import TitleSectionComponent from '../../../../../../components/intern/TitleSectionComponent/TitleSectionComponent';
import EditableFieldComponent from '../../../../../../components/EditableField/EditableField';
import AlertComponent from '../../../../../../components/intern/Alert/Alert';
import './AdminConfig.css';

const defaultConfig = {
  precoHora: 40,
  horasPorPeso: 2,
  cotacao: 6,
  moeda: 'USD',
  prioridades: { baixa: 0, media: 10, alta: 20 },
  precoPorLinhaPowerfeed: 0.01,
  links: {
    pdfLink: ''
  },
  updatedAt: '' // Mantive apenas updatedAt como string vazia
};

const AdminConfig = ({ user }) => {
  const dispatch = useDispatch();
  const { configStore, loading, error } = useSelector((state) => state.configStore);

  useEffect(() => {
    dispatch(fetchConfig());
  }, [dispatch]);

  const [config, setConfig] = useState({
    ...defaultConfig,
    prioridades: { ...defaultConfig.prioridades },
    links: { ...defaultConfig.links }
  });
  const [errors, setErrors] = useState({});
  const [alertData, setAlertData] = useState(null);
  const [originalConfig, setOriginalConfig] = useState({
    ...defaultConfig,
    prioridades: { ...defaultConfig.prioridades },
    links: { ...defaultConfig.links }
  });

  useEffect(() => {
    if (configStore && configStore.length > 0) {
      const firstConfig = configStore[0];
      const newConfig = {
        precoHora: firstConfig.precoHora || 40,
        horasPorPeso: firstConfig.horasPorPeso || 2,
        cotacao: firstConfig.cotacao || 6,
        moeda: firstConfig.moeda || 'USD',
        precoPorLinhaPowerfeed: firstConfig.precoPorLinhaPowerfeed || 0.01,
        links: {
          mockupSpecs: firstConfig.linkMockupSpecs || '',
          psdMockup: firstConfig.linkPsdMockup || '',
          entregaMockup: firstConfig.linkEntregaMockup || '',
          planilhaVersoes: firstConfig.linkPlanilhaVersoes || ''
        },
        prioridades: {
          baixa: firstConfig.prioridadeBaixa || 0,
          media: firstConfig.prioridadeMedia || 10,
          alta: firstConfig.prioridadeAlta || 20
        },
        updatedAt: firstConfig.updatedAt || '' // Removi o updatedBy aqui
      };

      setConfig(newConfig);
      setOriginalConfig(newConfig);
    }
  }, [configStore]);

  const hasChanges = JSON.stringify(config) !== JSON.stringify(originalConfig);

  const validate = (field, value) => {
    let error = '';
    const numericFields = [
      'precoHora', 'horasPorPeso', 'cotacao',
      'precoPorLinhaPowerfeed', 'baixa', 'media', 'alta'
    ];

    if (numericFields.includes(field) && (isNaN(value) || value < 0)) {
      error = 'Informe um valor numérico válido.';
    }
    if (field === 'moeda' && !value.trim()) {
      error = 'Informe o código da moeda.';
    }
    if (field in config.links) {
      const normalized = value.trim();
      const isExternal = /^https?:\/\/\S+$/i.test(normalized);
      const isInternal = /^\/[^\s]*$/.test(normalized);

      if (normalized && !isExternal && !isInternal) {
        error = 'Informe uma URL válida (http, https ou caminho interno iniciando com "/").';
      }
    }

    setErrors(prev => ({ ...prev, [field]: error }));
    return !error;
  };

  const handleChange = (key, value) => {
    setConfig(prev => ({ ...prev, [key]: value }));
    validate(key, value);
  };

  const handlePriorityChange = (nivel, value) => {
    setConfig(prev => ({
      ...prev,
      prioridades: { ...prev.prioridades, [nivel]: Number(value) }
    }));
    validate(nivel, value);
  };

  const handleLinkChange = (field, value) => {
    setConfig(prev => ({
      ...prev,
      links: { ...prev.links, [field]: value }
    }));
    validate(field, value);
  };

  const handleSave = async () => {
    if (!hasChanges) {
      setAlertData({ type: 'nenhumaAlteracao' });
      return;
    }

    try {
      if (!configStore || configStore.length === 0) {
        throw new Error('Nenhuma configuração carregada');
      }

      const configData = {
        id: configStore[0].id,
        precoHora: config.precoHora,
        horasPorPeso: config.horasPorPeso,
        cotacao: config.cotacao,
        moeda: config.moeda,
        prioridadeBaixa: config.prioridades.baixa,
        prioridadeMedia: config.prioridades.media,
        prioridadeAlta: config.prioridades.alta,
        precoPorLinhaPowerfeed: config.precoPorLinhaPowerfeed,
        pdfLink: config.links.pdfLink,
        updatedAt: new Date().toISOString()
      };

      await dispatch(updateConfig(configData));

      const updatedConfig = {
        ...config,
        updatedAt: configData.updatedAt
      };

      saveConfig(updatedConfig);
      setConfig(updatedConfig);
      setOriginalConfig(updatedConfig);

      setAlertData({ type: 'editar', message: 'Configuração salva com sucesso!' });
    } catch (error) {
      console.error('Erro ao salvar configuração:', error);
      setAlertData({ type: 'error', message: error.message || 'Erro ao salvar configuração' });
    }
  };

  const calculoFields = [
    {
      key: 'precoHora',
      label: `Preço por hora (${config.moeda})`,
      tooltip: 'Valor cobrado por cada hora de trabalho',
      type: 'number'
    },
    {
      key: 'horasPorPeso',
      label: 'Horas por peso',
      tooltip: 'Quantidade de horas que cada peso representa',
      type: 'number'
    },
    {
      key: 'cotacao',
      label: 'Cotação',
      tooltip: 'Valor da cotação',
      type: 'number'
    },
    /* {
       key: 'moeda',
       label: 'Moeda',
       tooltip: 'Código da moeda usada nos cálculos',
       type: 'text'
     },*/
    {
      key: 'precoPorLinhaPowerfeed',
      label: `Preço por linha, PowerFeed`,
      tooltip: 'Valor cobrado por cada linha de PowerFeed',
      type: 'number',
      step: '0.001'
    }
  ];

  if (loading) return <p>Carregando sistema... Por favor, aguarde.</p>;
  if (error) return <p>Erro: {error}</p>;
  if (!config) return <p>Carregando configurações...</p>;

  return (
    <section className="container-extern content-with-sidebar">
      {alertData && (
        <AlertComponent
          type={alertData.type}
          message={alertData.message}
          onClose={() => setAlertData(null)}
        />
      )}

      <TitlePageComponent
        title="Rules and Values Configuration"
        subtitle="Parameters that control the calculation logic and estimate generation"
      />

      <fieldset className="config-section">
        <TitleSectionComponent> Calculation Settings </TitleSectionComponent>
        <div className="config-fields-grid">
          {calculoFields.map(({ key, label, tooltip, type, step }) => (
            <EditableFieldComponent
              key={key}
              label={label}
              tooltip={tooltip}
              type={type}
              step={step}
              value={config[key]}
              onChange={val => handleChange(key, val)}
              error={errors[key]}
            />
          ))}
        </div>
        <fieldset className="config-section">
          <TitleSectionComponent> Priority (%) </TitleSectionComponent>
          <div className="config-fields-grid">
            {['baixa', 'media', 'alta'].map(nivel => (
              <EditableFieldComponent
                key={nivel}
                label={nivel.charAt(0).toUpperCase() + nivel.slice(1)}
                type="number"
                value={config.prioridades[nivel]}
                onChange={val => handlePriorityChange(nivel, val)}
                error={errors[nivel]}
              />
            ))}
          </div>
        </fieldset>
      </fieldset>

      <fieldset className="config-section">
        <TitleSectionComponent> Links for PDF </TitleSectionComponent>
        <div className="config-fields-grid">
          <EditableFieldComponent
            label="PDF Link"
            tooltip="Insert the link that will be shown in the proposal footer"
            type="url"
            value={config.links.pdfLink}
            onChange={val => handleLinkChange("pdfLink", val)}
            error={errors.pdfLink}
          />
        </div>
      </fieldset>

      <div className="button-container">
        <ButtonComponent
          type="button"
          variant="save"
          className="btn-save"
          onClick={handleSave}
          disabled={!hasChanges}
        >
          Save Changes
        </ButtonComponent>
      </div>

      {config.updatedAt && (
        <div className="last-update">
          <strong>Last updated:</strong> {new Date(config.updatedAt).toLocaleString()}
        </div>
      )}
    </section>
  );
};

export default AdminConfig;